import algoliasearch from 'algoliasearch';
import PropTypes from 'prop-types';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { getAlgoliaToken } from '../../services/algolia';

const isExpiredKeyError = (error) =>
  error?.status === 403 || /invalid api key|not enough acl|key.*expired/i.test(error?.message || '');

const decodeValidUntil = (apiKey) => {
  try {
    const decoded =
      typeof window !== 'undefined' ? window.atob(apiKey) : Buffer.from(apiKey, 'base64').toString('utf-8');
    const validUntil = Number(new URLSearchParams(decoded.slice(64)).get('validUntil'));
    return Number.isFinite(validUntil) && validUntil > 0 ? validUntil : null;
  } catch (_error) {
    return null;
  }
};

const AlgoliaContext = createContext({
  client: { initIndex: () => ({ search: async () => ({ hits: [] }) }) },
  refreshToken: () => {},
});

export const AlgoliaProvider = ({ children }) => {
  const apiKeyRef = useRef(process.env.GATSBY_ALGOLIA_SEARCH_KEY);
  const pendingRefreshRef = useRef(null);
  const refreshTimeoutRef = useRef(null);
  const refreshTokenRef = useRef(null);
  // Résolue une fois que la toute première tentative de récupération du token (au montage) est
  // tranchée (succès -> clé sécurisée, échec/absence -> repli sur la clé publique déjà en place).
  // Les recherches attendent cette résolution pour ne jamais interroger Algolia avec un état de
  // clé encore indéterminé (source du résultat vide aléatoire au remount du Provider).
  const initialTokenReadyRef = useRef(null);

  const scheduleRefresh = useCallback((apiKey) => {
    clearTimeout(refreshTimeoutRef.current);
    const validUntil = decodeValidUntil(apiKey);
    if (!validUntil) {
      return;
    }
    const delay = Math.max(validUntil * 1000 - Date.now() - 60_000, 5_000);
    refreshTimeoutRef.current = setTimeout(() => refreshTokenRef.current(), delay);
  }, []);

  const refreshToken = useCallback(() => {
    if (!pendingRefreshRef.current) {
      pendingRefreshRef.current = getAlgoliaToken()
        .then((token) => {
          if (token) {
            apiKeyRef.current = token;
            scheduleRefresh(token);
          }
          return token;
        })
        .finally(() => {
          pendingRefreshRef.current = null;
        });
    }
    return pendingRefreshRef.current;
  }, [scheduleRefresh]);
  refreshTokenRef.current = refreshToken;

  if (initialTokenReadyRef.current === null) {
    initialTokenReadyRef.current = typeof window !== 'undefined' ? refreshToken() : Promise.resolve();
  }

  useEffect(() => () => clearTimeout(refreshTimeoutRef.current), []);

  const client = useMemo(() => {
    const buildIndex = (indexName) =>
      algoliasearch(process.env.GATSBY_ALGOLIA_APP_ID, apiKeyRef.current).initIndex(indexName);

    const wrapIndex = (indexName) => ({
      search: async (query, params) => {
        if (initialTokenReadyRef.current) {
          await initialTokenReadyRef.current;
        }
        try {
          return await buildIndex(indexName).search(query, params);
        } catch (error) {
          if (!isExpiredKeyError(error)) {
            throw error;
          }
          await refreshToken();
          return buildIndex(indexName).search(query, params);
        }
      },
    });

    return { initIndex: wrapIndex };
  }, [refreshToken]);

  const value = useMemo(() => ({ client, refreshToken }), [client, refreshToken]);

  return <AlgoliaContext.Provider value={value}>{children}</AlgoliaContext.Provider>;
};

AlgoliaProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAlgoliaClient = () => useContext(AlgoliaContext);
